import React from 'react';

import { Popper, Paper, ClickAwayListener } from '@material-ui/core';
import PriorityHighIcon from '@material-ui/icons/PriorityHigh';
import styled from 'styled-components';
import { Button } from 'styles/admin';
import {
  ColoredCircularProgress,
  FlexContainer,
  TinySizeCSS,
  StyledBox,
  VerticallyAlignedFlexContainer,
} from 'styles/common';

import { CheckIcon, StyledIcon } from 'constants/icons';
import theme from 'constants/theme';

import { Typography2, Typography3 } from 'uiToolkit/Typography';

const FileDownloadPopper = React.forwardRef(
  (
    {
      open,
      handleClickAway,
      fileName,
      requestData,
      onSuccess,
      onError,
      isFetching,
      downloadBlob,
      onDownloadComplete,
      hasError,
      isStatusData,
      setDownloadRequestsInLS,
      reportType,
      downloadText,
      localStorageKey,
    },
    ref
  ) => {
    const setGeneratedFilesToLS = (res) => {
      try {
        const prevGeneratedFiles = JSON.parse(window.localStorage.getItem(localStorageKey)) ?? [];

        const generatedFiles = [...prevGeneratedFiles, { ...res, requestedTS: Date.now() }];

        const stringifiedGeneratedFiles = JSON.stringify(generatedFiles);

        window.localStorage.setItem(localStorageKey, stringifiedGeneratedFiles);
        setDownloadRequestsInLS(generatedFiles);
      } catch (e) {
        console.error('Error in LS : ', e);
      }
    };

    const generateFile = async () => {
      try {
        if (isFetching) return;
        const response = await requestData();

        setGeneratedFilesToLS(response.entity);
        // const contentDispositionString = response.headers.get('content-disposition');
        // fileName = contentDispositionString.split('filename=')?.[1]?.split(';')?.[0] || fileName;
        // const fetchedBlob = await response.blob();
        // onSuccess({ fetchedBlob, fileName });
      } catch (e) {
        onError(e, reportType);
      }
    };

    const downloadFile = async (e) => {
      e.stopPropagation();
      try {
        if (downloadBlob) {
          const { fetchedBlob: blob, fileName, requestId } = downloadBlob;
          // 2. Create blob link to download
          const url = window.URL.createObjectURL(new Blob([blob], { type: 'text/csv' }));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', fileName);
          // 3. Append to html page
          document.body.appendChild(link);
          // 4. Force download
          link.click();
          // 5. Clean up and remove the link
          link.parentNode.removeChild(link);
          onDownloadComplete(requestId, reportType);
          handleClickAway();
        }
      } catch (e) {
        onError(e, reportType);
      }
    };

    return (
      <ClickAwayListener onClickAway={handleClickAway}>
        <Popper
          open={open}
          placement="bottom-end"
          anchorEl={ref?.current}
          transition
          style={{ zIndex: 5 }}
          modifiers={{
            offset: {
              enabled: true,
              offset: '15, 2',
            },
          }}
        >
          <StyledPaper elevation={0}>
            <StyledBox>
              <FlexContainer>
                <Typography2 color={theme.palette.text.heading}>{downloadText}</Typography2>
              </FlexContainer>
              <Divider />

              <VerticallyAlignedFlexContainer style={{ padding: '0.5rem 0' }}>
                {isFetching && !downloadBlob ? (
                  <VerticallyAlignedFlexContainer>
                    <ColoredCircularProgress color="black" size="header" style={{ marginRight: '8px' }} />

                    <Typography3 color={theme.palette.text.primary}>Creating your file for download</Typography3>
                  </VerticallyAlignedFlexContainer>
                ) : (
                  <>
                    {!hasError ? (
                      <>
                        {!downloadBlob ? (
                          <FlexContainer style={{ justifyContent: 'center', width: '100%' }}>
                            <Button blueOutline size="large" onClick={generateFile} style={{ width: '100%' }}>
                              <Typography2 color="primary">CSV</Typography2>
                            </Button>
                          </FlexContainer>
                        ) : (
                          <FlexContainer style={{ flexDirection: 'column' }}>
                            <FlexContainer style={{ justifyContent: 'flex-start' }}>
                              <IconContainer>
                                <CheckIcon
                                  style={{ margin: '0 auto' }}
                                  size="tiniest"
                                  color={theme.palette.icons.white}
                                />
                              </IconContainer>
                              <Typography3 style={{ marginLeft: '8px' }} color={theme.palette.text.primary}>
                                Completed
                              </Typography3>
                            </FlexContainer>
                            <Typography2 topGutter="1" color={theme.palette.text.primary}>
                              User data CSV is ready for download
                            </Typography2>

                            <Button
                              onClick={downloadFile}
                              disableRipple
                              style={{ width: '30%', padding: '0', marginTop: '5px' }}
                              color="primary"
                            >
                              Download
                            </Button>
                          </FlexContainer>
                        )}
                      </>
                    ) : (
                      <FlexContainer style={{ flexDirection: 'column' }}>
                        <FlexContainer style={{ justifyContent: 'flex-start' }}>
                          <IconContainer isError>
                            <StyledIcon style={{ margin: '0 auto' }} size="tiniest" color={theme.palette.error.main}>
                              <PriorityHighIcon />
                            </StyledIcon>
                          </IconContainer>
                          <Typography3 leftGutter="1" color={theme.palette.text.primary}>
                            Failed
                          </Typography3>
                        </FlexContainer>
                        <Typography2 topGutter="1" color={theme.palette.text.primary}>
                          File computation failed. Please try again later.
                        </Typography2>
                      </FlexContainer>
                    )}
                  </>
                )}
              </VerticallyAlignedFlexContainer>
            </StyledBox>
          </StyledPaper>
        </Popper>
      </ClickAwayListener>
    );
  }
);

const IconContainer = styled.div`
  ${TinySizeCSS};
  display: flex;
  border-radius: 50%;
  padding: 2px;
  background-color: ${(props) => (!props.isError ? props.theme.palette.success.main : '')};
  border: ${(props) => (props.isError ? `1px solid ${props.theme.palette.error.main}` : '')};
`;

const StyledPaper = styled(Paper)`
  background-color: ${(props) => props.theme.palette.background.paper};
  padding: 1rem;
  box-shadow: 0px 2px 5px rgba(0, 0, 0, 0.11);
  border: 1px solid ${theme.palette.borders.lighterGrey};
  min-width: 18rem;
`;
const Divider = styled.div`
  border-bottom: 1px solid ${theme.palette.borders.lighterGrey};
  margin: 1rem 0 0.6rem 0;
`;

export default FileDownloadPopper;
